import { useMemo, useEffect } from "react";
import ReactECharts from "echarts-for-react";
import { getChartPalette, chartStateShell } from "./chartTheme";

type PieChartProps = {
  title: string;
  xLabel: string;
  yLabel: string;
  data: any;
  width?: string | number;
  height?: string | number;
  isFetching?: boolean;
  isDarkMode?: boolean;
};

const PieChart = ({
  title,
  xLabel,
  yLabel,
  data,
  width = '100%',
  height = 350,
  isFetching,
  isDarkMode = true,
}: PieChartProps) => {
  const formattedData = useMemo(
    () => (typeof data === "object" && data !== null ? data : {}),
    [data]
  );

  const chartData = useMemo(() => {
    const eventWise = formattedData.event_wise || { categories: [], series: [] };
    const categories = eventWise.categories || [];
    const series = eventWise.series || [];

    // Check if categories array is empty or has length <= 0
    if (!categories || categories.length <= 0) return [];

    // Check if series array is empty or has length <= 0
    if (!series || series.length <= 0) return [];

    return categories.map((label: string, index: number) => ({
      name: label,
      value: series[index] || 0,
    }));
  }, [formattedData]);

  // Check if response is successful but categories array is empty
  const isNoDataFound = useMemo(() => {
    const eventWise = formattedData.event_wise || { categories: [], series: [] };
    const categories = eventWise.categories || [];
    const series = eventWise.series || [];
    
    // If categories array length is 0 or less OR series array length is 0 or less, show "No data found"
    return categories.length <= 0 || series.length <= 0;
  }, [formattedData]);

  const option = useMemo(() => {
    const c = getChartPalette(isDarkMode);
    return {
      textStyle: { fontFamily: 'Open Sans, Arial, sans-serif' },
      tooltip: {
        trigger: 'item',
        formatter: '{a} <br/>{b}: {c} ({d}%)',
        textStyle: {
          fontSize: 10,
          fontFamily: 'Open Sans, Arial, sans-serif',
          fontWeight: 'normal',
        },
      },
      legend: {
        type: 'scroll',
        orient: 'horizontal',
        bottom: 0,
        left: 'center',
        textStyle: {
          color: c.secondaryTextColor,
          fontSize: 11,
          fontFamily: 'Arial',
          fontWeight: 'normal',
        },
        itemWidth: 10,
        itemHeight: 10,
        itemGap: 8,
      },
      series: [
        {
          name: title,
          type: 'pie',
          radius: ['40%', '70%'],
          center: ['50%', '50%'],
          data: chartData,
          label: {
            show: true,
            position: 'outside',
            formatter: '{b}\n{d}%',
            fontSize: 11,
            fontFamily: 'Arial, sans-serif',
            fontWeight: 'normal',
            color: c.secondaryTextColor,
          },
          labelLine: {
            show: true,
            length: 10,
            length2: 5,
          },
          itemStyle: {
            borderColor: c.backgroundColor,
            borderWidth: 0,
          },
          emphasis: {
            focus: false,
            label: {
              show: true,
              fontWeight: 'bold',
              fontFamily: 'Arial',
            },
          },
          animation: true,
          animationDuration: 1500,
          animationEasing: 'cubicOut',
        },
      ],
      color: c.colors,
    };
  }, [title, chartData, isDarkMode]);

  useEffect(() => {
    const applyNormalFont = () => {
      const textElements = document.querySelectorAll('.echarts-for-react text');
      textElements.forEach((text) => {
        (text as HTMLElement).style.fontWeight = 'normal';
        (text as HTMLElement).style.fontSize = '11px';
        (text as HTMLElement).style.fontFamily = 'Arial, sans-serif';
      });
    };
    applyNormalFont();
    const timer = setTimeout(applyNormalFont, 300);
    return () => clearTimeout(timer);
  }, []);

  // Show loading message when request is going to backend/response not coming yet
  if (isFetching) {
    return (
      <div className={chartStateShell}>
        <div className="px-4 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-300 border-t-blue-500 dark:border-slate-500" />
          </div>
          <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
            Loading chart data...
          </p>
          <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
            Please wait while we prepare this visualization.
          </p>
        </div>
      </div>
    );
  }

  // Show "no data" message when response comes successfully but categories/series are empty
  if (isNoDataFound) {
    return (
      <div className={chartStateShell}>
        <div className="px-4 text-center">
          <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
            No data found for this chart.
          </p>
          <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
            Adjust your filters or timeframe to see event distribution here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: '58vh', width: '100%' }}>
    <ReactECharts
      option={option}
      style={{ height: '98%', width: "100%" }}
      opts={{ renderer: "canvas" }}
      className="chart-container no-hover pie-chart"
    />
     </div>
  );
};

export default PieChart;
